import React, { useMemo } from "react";
import { Divider } from "@mui/material";
import { styled } from "@mui/material/styles";

/** Subcomponents */
import { SeriesSourceSelector } from "components/DataExplorationV2/ConfigureMenu/SeriesSection/SeriesSourceSelector";
import { SiteAnalysis } from "components/DataExplorationV2/ConfigureMenu/SeriesSection/SiteAnalysis";
import { CurrentSeries } from "components/DataExplorationV2/ConfigureMenu/SeriesSection/CurrentSeries";
import { AddSeries } from "components/DataExplorationV2/ConfigureMenu/SeriesSection/AddSeries";

/** Typescript Interfaces */
import { Tag } from "interfaces/tag";
import { Machine } from "interfaces/machine";
import { Component } from "interfaces/component";
import { LineSeries, ScadaDatetimeTagDataSource } from "interfaces/chart";

export interface ConfigureSeriesProps {
  /** The status of API fetch for Parameters(Tags) and Machines */
  loading: boolean;
  error: boolean;
  /** Component related props */
  components: Component[] | null;
  /** Parameter(Tag) related props */
  parameters: Tag[] | null;
  selectedParameter: Tag | null;
  onChangeParameter: (newParameter: Tag | null) => void;
  /** Machine related props */
  machines: Machine[] | null;
  selectedMachine: Machine | null;
  onChangeMachine: (newMachine: Machine | null) => void;
  /** Site configure analysis props */
  siteAverageEnabled: boolean;
  siteRangeEnabled: boolean;
  onChangeSiteAverageEnabled: (newValue: boolean) => void;
  onChangeSiteRangeEnabled: (newValue: boolean) => void;
  /** Series related props */
  series: any[] | null;
  selectedSeries: LineSeries<ScadaDatetimeTagDataSource> | null;
  onChangeSelectedSeries: (
    series: LineSeries<ScadaDatetimeTagDataSource>
  ) => void;
  onAddSeries: () => void;
  onDeleteSeries: (seriesId: string) => void;
}

export const ConfigureSeriesSection = ({
  loading,
  error,
  components,
  parameters,
  machines,
  selectedParameter,
  selectedMachine,
  onChangeParameter,
  onChangeMachine,
  siteAverageEnabled,
  siteRangeEnabled,
  onChangeSiteAverageEnabled,
  onChangeSiteRangeEnabled,
  series,
  selectedSeries,
  onChangeSelectedSeries,
  onAddSeries,
  onDeleteSeries
}: ConfigureSeriesProps): JSX.Element => {
  const handleDeleteSeries = (
    event: React.UIEvent<{}>,
    seriesId: string
  ): void => {
    // Prevent selecting series
    event.stopPropagation();
    onDeleteSeries(seriesId);
  };

  const filteredMachines = useMemo(() => {
    if (selectedParameter && machines) {
      return machines.filter(machine =>
        machine.scalar_configurations.includes(
          selectedParameter.scalar_configuration
        )
      );
    }
    return machines;
  }, [machines, selectedParameter]);

  const filteredParameters = useMemo(() => {
    if (selectedMachine && parameters) {
      return parameters.filter(parameter =>
        selectedMachine.scalar_configurations.includes(
          parameter.scalar_configuration
        )
      );
    }
    return parameters;
  }, [parameters, selectedMachine]);

  return (
    <SelectionWrap>
      {/* Add Series button and label */}
      <AddSeries
        loading={loading}
        error={error}
        series={series}
        selectedSeries={selectedSeries}
        onAddSeries={onAddSeries}
        onChangeSelectedSeries={onChangeSelectedSeries}
        onClickDeleteSeries={handleDeleteSeries}
      />

      {/* Series parameter and machine selectors */}
      <SeriesSourceSelector
        loading={loading}
        error={error}
        components={components}
        parameters={filteredParameters}
        selectedParameter={selectedParameter}
        onChangeParameter={onChangeParameter}
        machines={filteredMachines}
        selectedMachine={selectedMachine}
        onChangeMachine={onChangeMachine}
        selectedSeries={selectedSeries}
      />

      {/* Site average and range */}
      <SiteAnalysis
        siteAverageEnabled={siteAverageEnabled}
        onChangeSiteAverageEnabled={onChangeSiteAverageEnabled}
        siteRangeEnabled={siteRangeEnabled}
        onChangeSiteRangeEnabled={onChangeSiteRangeEnabled}
      />

      <StyledDivider />

      {/* Series edit and delete */}
      <CurrentSeries
        loading={loading}
        error={error}
        series={series}
        selectedSeries={selectedSeries}
        onAddSeries={onAddSeries}
        onChangeSelectedSeries={onChangeSelectedSeries}
        onClickDeleteSeries={handleDeleteSeries}
      />
    </SelectionWrap>
  );
};

const SelectionWrap = styled("div")`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: ${({ theme }) => theme.spacing(2)};
  overflow: hidden;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
`;

const StyledDivider = styled(Divider)`
  &.MuiDivider-root {
    margin: 0 -${({ theme }) => theme.spacing(2)} -${({ theme }) =>
        theme.spacing(1)};
  }
`;
